/**
 * User preferences management for profile.
 *
 * Contract: preferences on the profile are stored as a single JSON string. The backend
 * REPLACES the whole preferences object when we send it. So:
 * 1. Always read current preferences from the profile (incoming data).
 * 2. Modify to get "our" version (set/merge/delete keys).
 * 3. Send the FULL preferences object back in the profile update.
 *
 * Structure:
 * - menu_history: selected menu items by date (past and future); full item snapshot + timestamps + ids.
 * - subscription_history: subscription snapshots from present onwards (sub + day config, freeze, double box, etc.).
 * - metrics: user-added or auto-captured metrics (water, steps, etc.) with timestamps and ids.
 * All entries use unique ids and timestamps for tracking.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';

// ---------------------------------------------------------------------------
// IDs and timestamps
// ---------------------------------------------------------------------------

export type PreferenceId = string;

export function generatePreferenceId(): PreferenceId {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

// ---------------------------------------------------------------------------
// 1. Menu history (date-wise selected items; includes future dates)
// ---------------------------------------------------------------------------

export interface MenuSelectedItemSnapshot {
  id: PreferenceId;
  recorded_at: string; // ISO
  menu_item_id: number;
  menu_builder_id: number;
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  food_type_id: number;
  food_type_name: string;
  food_type_category: string;
  quantity: number;
  selection_number?: number;
  slug?: string;
}

export interface MenuDayEntry {
  date: string; // YYYY-MM-DD
  items: MenuSelectedItemSnapshot[];
  updated_at: string; // ISO
}

export type MenuHistory = Record<string, MenuDayEntry>; // key = date YYYY-MM-DD

// ---------------------------------------------------------------------------
// 2. Subscription history (snapshots when user has active sub; no API history)
// ---------------------------------------------------------------------------

export interface SubscriptionSnapshot {
  id: PreferenceId;
  recorded_at: string; // ISO
  subscription_id: number;
  customer_id: number;
  start_date: string;
  end_date: string;
  status: string;
  freeze_dates: string[];
  double_box_dates: string[];
  cost?: number;
  price?: number;
  title?: string;
  day_config_snapshot?: Record<string, unknown>; // day config for that sub
  detail?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// 3. Metrics (water, steps, etc.; manual or auto; for future module)
// ---------------------------------------------------------------------------

export type MetricSource = 'manual' | 'auto';

export interface MetricEntry {
  id: PreferenceId;
  recorded_at: string; // ISO
  date: string; // YYYY-MM-DD the metric is for
  metric_type: string; // e.g. 'water_glasses' | 'steps' | 'sleep_hours'
  value: number | string;
  source?: MetricSource;
  unit?: string;
  extra?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// UserPreferences (single source of shape)
// ---------------------------------------------------------------------------

/** Persisted dashboard master data (subscription dataset + day_config_map, etc.). Stored as one object in preferences. */
export type MasterDataPref = Record<string, unknown> | null;

export interface UserPreferences {
  updated_at?: string;
  theme?: 'light' | 'dark' | 'system';
  menu_history?: MenuHistory;
  subscription_history?: SubscriptionSnapshot[];
  metrics?: MetricEntry[];
  /** Single object: full masterData for dashboard. Source of truth; persisted with profile. */
  master_data?: MasterDataPref;
}

const ALLOWED_KEYS: (keyof UserPreferences)[] = [
  'updated_at',
  'theme',
  'menu_history',
  'subscription_history',
  'metrics',
  'master_data',
];

const ALLOWED_KEYS_SET = new Set<string>(ALLOWED_KEYS);

function isAllowedKey(key: string): key is keyof UserPreferences {
  return ALLOWED_KEYS_SET.has(key);
}

function isValidPreferenceValue(value: unknown, key: keyof UserPreferences): boolean {
  if (value === undefined || value === null) {return true;}
  if (key === 'theme') {
    return value === 'light' || value === 'dark' || value === 'system';
  }
  if (key === 'updated_at') {return typeof value === 'string';}
  if (key === 'menu_history') {return typeof value === 'object' && value !== null && !Array.isArray(value);}
  if (key === 'subscription_history') {return Array.isArray(value);}
  if (key === 'metrics') {return Array.isArray(value);}
  if (key === 'master_data') {return value === null || (typeof value === 'object' && value !== null && !Array.isArray(value));}
  return false;
}

// ---------------------------------------------------------------------------
// Normalizers (raw API/cache -> typed shape)
// ---------------------------------------------------------------------------

function normalizeMenuSelectedItem(raw: unknown): MenuSelectedItemSnapshot | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {return null;}
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : generatePreferenceId();
  const recorded_at = typeof o.recorded_at === 'string' ? o.recorded_at : new Date().toISOString();
  const menu_item_id = Number(o.menu_item_id);
  const menu_builder_id = Number(o.menu_builder_id);
  if (!Number.isFinite(menu_item_id)) {return null;}
  return {
    id,
    recorded_at,
    menu_item_id,
    menu_builder_id: Number.isFinite(menu_builder_id) ? menu_builder_id : 0,
    name: String(o.name ?? ''),
    calories: Number(o.calories) || 0,
    protein: Number(o.protein) || 0,
    carbs: Number(o.carbs) || 0,
    fats: Number(o.fats) || 0,
    food_type_id: Number(o.food_type_id ?? o.meal_type_id) || 0,
    food_type_name: String(o.food_type_name ?? o.meal_type_name ?? ''),
    food_type_category: String(o.food_type_category ?? o.meal_type_category ?? ''),
    quantity: Number(o.quantity) || 0,
    selection_number: o.selection_number != null ? Number(o.selection_number) : undefined,
    slug: typeof o.slug === 'string' ? o.slug : undefined,
  };
}

function normalizeMenuDayEntry(date: string, raw: unknown): MenuDayEntry | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {return null;}
  const o = raw as Record<string, unknown>;
  const itemsRaw = Array.isArray(o.items) ? o.items : [];
  const items = itemsRaw.map(normalizeMenuSelectedItem).filter((x): x is MenuSelectedItemSnapshot => x !== null);
  const updated_at = typeof o.updated_at === 'string' ? o.updated_at : new Date().toISOString();
  return { date, items, updated_at };
}

function normalizeMenuHistory(raw: unknown): MenuHistory {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {return {};}
  const result: MenuHistory = {};
  const obj = raw as Record<string, unknown>;
  for (const date of Object.keys(obj)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {continue;}
    const entry = normalizeMenuDayEntry(date, obj[date]);
    if (entry) {result[date] = entry;}
  }
  return result;
}

function normalizeSubscriptionSnapshot(raw: unknown): SubscriptionSnapshot | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {return null;}
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : generatePreferenceId();
  const recorded_at = typeof o.recorded_at === 'string' ? o.recorded_at : new Date().toISOString();
  const subscription_id = Number(o.subscription_id);
  const customer_id = Number(o.customer_id);
  if (!Number.isFinite(subscription_id)) {return null;}
  return {
    id,
    recorded_at,
    subscription_id,
    customer_id: Number.isFinite(customer_id) ? customer_id : 0,
    start_date: String(o.start_date ?? ''),
    end_date: String(o.end_date ?? ''),
    status: String(o.status ?? ''),
    freeze_dates: Array.isArray(o.freeze_dates) ? o.freeze_dates.map(String) : [],
    double_box_dates: Array.isArray(o.double_box_dates) ? o.double_box_dates.map(String) : [],
    cost: o.cost != null ? Number(o.cost) : undefined,
    price: o.price != null ? Number(o.price) : undefined,
    title: typeof o.title === 'string' ? o.title : undefined,
    day_config_snapshot: o.day_config_snapshot && typeof o.day_config_snapshot === 'object' ? (o.day_config_snapshot as Record<string, unknown>) : undefined,
    detail: o.detail && typeof o.detail === 'object' ? (o.detail as Record<string, unknown>) : undefined,
  };
}

function normalizeSubscriptionHistory(raw: unknown): SubscriptionSnapshot[] {
  if (!Array.isArray(raw)) {return [];}
  return raw.map(normalizeSubscriptionSnapshot).filter((x): x is SubscriptionSnapshot => x !== null);
}

function normalizeMetricEntry(raw: unknown): MetricEntry | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {return null;}
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : generatePreferenceId();
  const recorded_at = typeof o.recorded_at === 'string' ? o.recorded_at : new Date().toISOString();
  const date = typeof o.date === 'string' ? o.date : '';
  const metric_type = String(o.metric_type ?? '');
  if (!date || !metric_type) {return null;}
  return {
    id,
    recorded_at,
    date,
    metric_type,
    value: typeof o.value === 'number' ? o.value : String(o.value ?? ''),
    source: o.source === 'manual' || o.source === 'auto' ? o.source : undefined,
    unit: typeof o.unit === 'string' ? o.unit : undefined,
    extra: o.extra && typeof o.extra === 'object' ? (o.extra as Record<string, unknown>) : undefined,
  };
}

function normalizeMetrics(raw: unknown): MetricEntry[] {
  if (!Array.isArray(raw)) {return [];}
  return raw.map(normalizeMetricEntry).filter((x): x is MetricEntry => x !== null);
}

/**
 * Parse and validate preferences from the API. Strips unknown keys; normalizes nested structures.
 */
export function parseAndValidatePreferences(
  raw: string | Record<string, unknown> | undefined,
): UserPreferences {
  const parsed = parsePreferences(raw);
  const result: UserPreferences = {};
  for (const key of ALLOWED_KEYS) {
    const value = parsed[key];
    if (value === undefined || value === null) {continue;}
    if (key === 'menu_history') {
      result.menu_history = normalizeMenuHistory(value);
      continue;
    }
    if (key === 'subscription_history') {
      result.subscription_history = normalizeSubscriptionHistory(value);
      continue;
    }
    if (key === 'metrics') {
      result.metrics = normalizeMetrics(value);
      continue;
    }
    if (key === 'master_data') {
      result.master_data =
        value === null || (typeof value === 'object' && value !== null && !Array.isArray(value))
          ? (value as MasterDataPref)
          : undefined;
      continue;
    }
    if (!isValidPreferenceValue(value, key)) {continue;}
    (result as Record<string, unknown>)[key] = value;
  }
  return result;
}

/**
 * Get a preference value. Only allowed keys return typed values; others undefined.
 */
export function getPreferenceTyped<K extends keyof UserPreferences>(
  prefs: UserPreferences,
  key: K,
): UserPreferences[K] | undefined {
  if (!isAllowedKey(key)) {return undefined;}
  return prefs[key];
}

/**
 * Set a single preference. Only allowed keys with correct value types are accepted.
 * Nested structures (menu_history, subscription_history, metrics) are normalized before storing.
 */
export function setPreferenceTyped<K extends keyof UserPreferences>(
  current: UserPreferences,
  key: K,
  value: UserPreferences[K],
): UserPreferences {
  if (!isAllowedKey(key)) {return { ...current };}
  if (value === undefined || value === null) {return { ...current, [key]: undefined };}
  let normalized: UserPreferences[K] = value;
  if (key === 'menu_history') {normalized = normalizeMenuHistory(value) as UserPreferences[K];}
  else if (key === 'subscription_history') {normalized = normalizeSubscriptionHistory(value) as UserPreferences[K];}
  else if (key === 'metrics') {normalized = normalizeMetrics(value) as UserPreferences[K];}
  else if (!isValidPreferenceValue(value, key)) {return { ...current };}
  return { ...current, [key]: normalized };
}

/**
 * Merge multiple preferences. Only allowed keys with valid types are applied; nested structures normalized.
 */
export function mergePreferencesTyped(
  current: UserPreferences,
  updates: Partial<UserPreferences>,
): UserPreferences {
  let result = { ...current };
  for (const key of ALLOWED_KEYS) {
    const value = updates[key];
    if (value === undefined) {continue;}
    if (key === 'menu_history') {
      result = { ...result, menu_history: normalizeMenuHistory(value) };
      continue;
    }
    if (key === 'subscription_history') {
      result = { ...result, subscription_history: normalizeSubscriptionHistory(value) };
      continue;
    }
    if (key === 'metrics') {
      result = { ...result, metrics: normalizeMetrics(value) };
      continue;
    }
    if (!isValidPreferenceValue(value, key)) {continue;}
    result = { ...result, [key]: value };
  }
  return result;
}

/**
 * Remove a key from preferences. Only allowed keys are removed.
 */
export function deletePreferenceTyped<K extends keyof UserPreferences>(
  current: UserPreferences,
  key: K,
): UserPreferences {
  if (!isAllowedKey(key)) {return { ...current };}
  const result = { ...current };
  delete result[key];
  return result;
}

// ---------------------------------------------------------------------------
// Helpers: menu history
// ---------------------------------------------------------------------------

/**
 * Set or replace menu selection for a date. Use after user confirms/changes menu for that date.
 * Items should be snapshots with id and recorded_at (or they will be generated).
 */
export function setMenuDay(
  current: UserPreferences,
  date: string, // YYYY-MM-DD
  items: (MenuSelectedItemSnapshot | Omit<MenuSelectedItemSnapshot, 'id' | 'recorded_at'>)[],
): UserPreferences {
  const now = new Date().toISOString();
  const snapshots: MenuSelectedItemSnapshot[] = items.map(item => {
    const hasId = 'id' in item && typeof item.id === 'string';
    const hasRecorded = 'recorded_at' in item && typeof item.recorded_at === 'string';
    const normalized = normalizeMenuSelectedItem({
      ...item,
      id: hasId ? item.id : generatePreferenceId(),
      recorded_at: hasRecorded ? item.recorded_at : now,
    });
    return normalized!;
  });
  const menu_history: MenuHistory = { ...(current.menu_history ?? {}) };
  menu_history[date] = { date, items: snapshots, updated_at: now };
  return { ...current, menu_history, updated_at: now };
}

/**
 * Get menu day entry for a date, if any.
 */
export function getMenuDay(prefs: UserPreferences, date: string): MenuDayEntry | undefined {
  return prefs.menu_history?.[date];
}

// ---------------------------------------------------------------------------
// Helpers: subscription history
// ---------------------------------------------------------------------------

/**
 * Append a subscription snapshot to history. Call when user has an active subscription
 * (e.g. when we have sub + day config from API). Id and recorded_at added if missing.
 */
export function addSubscriptionSnapshot(
  current: UserPreferences,
  snapshot: SubscriptionSnapshot | Omit<SubscriptionSnapshot, 'id' | 'recorded_at'>,
): UserPreferences {
  const now = new Date().toISOString();
  const withMeta = { ...snapshot, id: (snapshot as SubscriptionSnapshot).id ?? generatePreferenceId(), recorded_at: (snapshot as SubscriptionSnapshot).recorded_at ?? now };
  const normalized = normalizeSubscriptionSnapshot(withMeta);
  if (!normalized) {return { ...current };}
  const list = [...(current.subscription_history ?? []), normalized];
  return { ...current, subscription_history: list, updated_at: now };
}

// ---------------------------------------------------------------------------
// Helpers: metrics
// ---------------------------------------------------------------------------

/**
 * Append a metric entry. Id and recorded_at added if missing.
 */
export function addMetricEntry(
  current: UserPreferences,
  entry: MetricEntry | Omit<MetricEntry, 'id' | 'recorded_at'>,
): UserPreferences {
  const now = new Date().toISOString();
  const withMeta = { ...entry, id: (entry as MetricEntry).id ?? generatePreferenceId(), recorded_at: (entry as MetricEntry).recorded_at ?? now };
  const normalized = normalizeMetricEntry(withMeta);
  if (!normalized) {return { ...current };}
  const list = [...(current.metrics ?? []), normalized];
  return { ...current, metrics: list, updated_at: now };
}

// ---------------------------------------------------------------------------
// Legacy untyped helpers (use typed API for new code)
// ---------------------------------------------------------------------------

export type PreferencesMap = Record<string, unknown>;

export function parsePreferences(raw: string | Record<string, unknown> | undefined): PreferencesMap {
  if (raw == null) {return {};}
  if (typeof raw === 'object' && !Array.isArray(raw)) {return { ...raw };}
  if (typeof raw !== 'string' || raw.trim() === '') {return {};}
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function stringifyPreferences(prefs: PreferencesMap | UserPreferences): string {
  return JSON.stringify(prefs);
}

export function getPreference(prefs: PreferencesMap, key: string): unknown {
  if (!key || !prefs) {return undefined;}
  const parts = key.split('.');
  let current: unknown = prefs;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') {return undefined;}
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export function setPreference(current: PreferencesMap, key: string, value: unknown): PreferencesMap {
  if (!key) {return { ...current };}
  const parts = key.split('.');
  const result = { ...current };
  let target: Record<string, unknown> = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    const existing = target[part];
    const nextObj =
      typeof existing === 'object' && existing !== null && !Array.isArray(existing)
        ? { ...(existing as Record<string, unknown>) }
        : {};
    target[part] = nextObj;
    target = nextObj as Record<string, unknown>;
  }
  target[parts[parts.length - 1]!] = value;
  return result;
}

export function deletePreference(current: PreferencesMap, key: string): PreferencesMap {
  if (!key || !current) {return { ...current };}
  const parts = key.split('.');
  const result = JSON.parse(JSON.stringify(current)) as Record<string, unknown>;
  let target: Record<string, unknown> = result;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    const next = target[part];
    if (next == null || typeof next !== 'object' || Array.isArray(next)) {return result;}
    target = next as Record<string, unknown>;
  }
  delete target[parts[parts.length - 1]!];
  return result;
}

export function mergePreferences(current: PreferencesMap, updates: Record<string, unknown>): PreferencesMap {
  let result = { ...current };
  for (const [key, value] of Object.entries(updates)) {
    result = setPreference(result, key, value);
  }
  return result;
}

export interface ProfileResponseLike {
  data?: { customer?: { preferences?: string; id?: number }; [key: string]: unknown };
  customer?: { preferences?: string; id?: number; [key: string]: unknown };
  [key: string]: unknown;
}

export function getPreferencesFromProfile(profile: ProfileResponseLike | null | undefined): PreferencesMap {
  if (!profile) {return {};}
  const raw =
    profile.data?.customer?.preferences ??
    (profile as { customer?: { preferences?: string } }).customer?.preferences;
  return parsePreferences(raw);
}

/**
 * Get validated UserPreferences from a profile API response.
 */
export function getTypedPreferencesFromProfile(
  profile: ProfileResponseLike | null | undefined,
): UserPreferences {
  if (!profile) {return {};}
  const raw =
    profile.data?.customer?.preferences ??
    (profile as { customer?: { preferences?: string } }).customer?.preferences;
  return parseAndValidatePreferences(raw);
}

/**
 * Build the payload for profile/update. Only allowed keys (UserPreferences) are included; rest stripped.
 */
export function buildProfileUpdatePayload(
  customer_id: number,
  fullPreferences: UserPreferences | PreferencesMap,
  otherFields?: Record<string, unknown>,
): Record<string, unknown> {
  const prefs =
    typeof fullPreferences === 'object' && fullPreferences !== null && !Array.isArray(fullPreferences)
      ? parseAndValidatePreferences(fullPreferences as Record<string, unknown>)
      : {};
  return {
    customer_id,
    preferences: stringifyPreferences(prefs),
    ...otherFields,
  };
}

// ---------------------------------------------------------------------------
// Preferences store (single source: profile preferences; masterData lives under master_data)
// ---------------------------------------------------------------------------

export interface PreferencesStoreState {
  preferences: UserPreferences;
  setPreferences: (p: UserPreferences) => void;
  /** Hydrate from profile API response or stored user. Call on app load and after GET profile. */
  loadFromProfile: (profile: ProfileResponseLike | null | undefined) => void;
}

export const usePreferencesStore = create<PreferencesStoreState>((set, get) => ({
  preferences: {},
  setPreferences: (p) => set({ preferences: p }),
  loadFromProfile: (profile) => {
    const incoming = getTypedPreferencesFromProfile(profile);
    const current = get().preferences;
    const merged = { ...incoming };
    if (incoming.master_data == null && current?.master_data != null) {
      merged.master_data = current.master_data;
    }
    set({ preferences: merged });
  },
}));

/** Read masterData from preferences (single source of truth). */
export function getMasterDataFromPreferences(): MasterDataPref {
  return usePreferencesStore.getState().preferences?.master_data ?? null;
}

/** Write masterData into preferences and return updated preferences. Caller should persist to profile (e.g. debounced). */
export function setMasterDataInPreferences(data: MasterDataPref): UserPreferences {
  const prefs = usePreferencesStore.getState().preferences;
  const next: UserPreferences = { ...prefs, master_data: data ?? null, updated_at: new Date().toISOString() };
  usePreferencesStore.getState().setPreferences(next);
  return next;
}

/** Current full preferences (e.g. for building profile/update payload). */
export function getCurrentPreferences(): UserPreferences {
  return usePreferencesStore.getState().preferences;
}

// ---------------------------------------------------------------------------
// Local backup (silent, on every profile/preferences touch)
// ---------------------------------------------------------------------------

const BACKUP_STORAGE_KEY = '@user_prefs_backups';
const MAX_BACKUP_BYTES = 10 * 1024 * 1024; // 10MB
const EVICT_PERCENT = 0.2; // free 20% when at limit
const TARGET_AFTER_EVICT = MAX_BACKUP_BYTES * (1 - EVICT_PERCENT); // 8MB

export type PreferencesBackupSource = 'api' | 'update';

export interface PreferencesBackupEntry {
  customer_id: number;
  timestamp: string; // ISO
  preferences: UserPreferences | Record<string, unknown>;
  profile_snapshot: unknown; // full profile at that time
  source: PreferencesBackupSource;
}

/** Approximate byte size (UTF-16: 2 bytes per char). Used for backup size limit. */
function byteLength(str: string): number {
  return str.length * 2;
}

async function loadBackupList(): Promise<PreferencesBackupEntry[]> {
  try {
    const raw = await AsyncStorage.getItem(BACKUP_STORAGE_KEY);
    if (!raw) {return [];}
    const parsed = JSON.parse(raw) as PreferencesBackupEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function saveBackupList(list: PreferencesBackupEntry[]): Promise<void> {
  await AsyncStorage.setItem(BACKUP_STORAGE_KEY, JSON.stringify(list));
}

function isSamePreferences(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Record a backup when preferences are received from the API or after a profile update.
 * Call this whenever you have profile data: after GET profile, or after POST profile/update.
 * Skips backup if the preferences JSON is identical to the last backup for the same customer_id.
 * Enforces size limit by evicting oldest entries when over 10MB, freeing 20%.
 */
export async function recordPreferencesBackup(
  profileData: ProfileResponseLike | null | undefined,
  source: PreferencesBackupSource,
): Promise<void> {
  if (!profileData) {return;}

  const customer =
    (profileData as { data?: { customer?: { id?: number } } }).data?.customer ??
    (profileData as { customer?: { id?: number } }).customer;
  const customer_id = customer?.id;
  if (typeof customer_id !== 'number') {return;}

  const preferences = getPreferencesFromProfile(profileData);
  const profile_snapshot = JSON.parse(JSON.stringify(profileData));

  const list = await loadBackupList();
  const lastForCustomer = list.filter(e => e.customer_id === customer_id).pop();
  if (lastForCustomer && isSamePreferences(lastForCustomer.preferences, preferences)) {
    return; // same as last backup for this customer, skip
  }

  const entry: PreferencesBackupEntry = {
    customer_id,
    timestamp: new Date().toISOString(),
    preferences,
    profile_snapshot,
    source,
  };
  list.push(entry);

  let payload = JSON.stringify(list);
  let size = byteLength(payload);

  while (size > MAX_BACKUP_BYTES && list.length > 0) {
    list.shift(); // remove oldest
    payload = JSON.stringify(list);
    size = byteLength(payload);
    if (size <= TARGET_AFTER_EVICT) {break;}
  }

  await saveBackupList(list);
}

/**
 * Get all stored backup entries (e.g. for restore UI or dex). Filter by customer_id if needed.
 */
export async function getPreferencesBackups(
  customer_id?: number,
): Promise<PreferencesBackupEntry[]> {
  const list = await loadBackupList();
  if (typeof customer_id === 'number') {
    return list.filter(e => e.customer_id === customer_id);
  }
  return list;
}
